import React, { useEffect, useState } from "react";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";

// computeTrend() — local fallback when backend Trend is not yet available.
// Backend (CategoryManager.ComputeTrend) is the primary source via the `trend` prop.
function computeTrend(chart, wellness) {
  if (!chart || chart.length === 0) return null;
  const scores = chart.map((c) => c?.score ?? c?.Score ?? 0).filter((s) => s > 0);
  if (scores.length === 0) return null;
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  const delta = scores.length > 1 ? scores[scores.length - 1] - scores[0] : 0;
  const label = avg >= 0.75 ? "Good" : avg >= 0.4 ? "Moderate" : wellness ? "Room to grow" : "Needs Attention";
  const color = avg >= 0.75 ? "#4CAF50" : avg >= 0.4 ? "#FF9800" : "#f44336";
  const direction = delta > 0.05 ? "↑ Improving" : delta < -0.05 ? "↓ Declining" : "→ Stable";
  return { Label: label, Color: color, Dot: "●", Avg: avg.toFixed(2), Direction: direction };
}

const LineChart = ({ title, color, chart, insight, trend, wellness, ...props }) => {
  const [data, setdata] = useState({ scoreArray: [], datesArray: [] });

  useEffect(() => {
    const scoreArray = [];
    const datesArray = [];
    for (let i = 0; i < chart?.length; i++) {
      scoreArray.push(chart[i]?.score ?? chart[i]?.Score ?? 0);
      const raw = chart[i]?.date ?? chart[i]?.Date;
      datesArray.push(raw ? String(raw).split("T")[0] : "");
    }
    setdata({ scoreArray, datesArray });
  }, [chart]);

  const resolvedColor =
    color !== undefined ? color
    : title === "Attention" ? "primary"
    : title === "Language" ? "secondary"
    : title === "Memory" ? "info"
    : title === "Orientation" ? "success"
    : title === "Reaction Time" ? "warning"
    : "error";

  // Backend trend takes priority; fall back to local computeTrend()
  const t = trend || computeTrend(chart, wellness);

  return (
    <MDBox mt={6} pr={1}>
      <ReportsLineChart
        color={resolvedColor}
        title={title}
        description={
          <MDBox>
            {insight && (
              <MDTypography variant="caption" color="text" lineHeight={1.5} display="block" mt={0.5} mb={1}>
                {insight}
              </MDTypography>
            )}
            {t ? (
              <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1} mt={0.5}>
                <MDTypography variant="caption" fontWeight="bold" style={{ color: t.Color || t.color, whiteSpace: "nowrap" }}>
                  {t.Dot || t.dot || "●"}&nbsp;{t.Label || t.label}
                </MDTypography>
                <MDTypography variant="caption" color="text" style={{ whiteSpace: "nowrap" }}>
                  Avg: {t.Avg || t.avg}
                </MDTypography>
                {(t.Direction || t.direction) && (
                  <MDTypography variant="caption" color="text" style={{ whiteSpace: "nowrap" }}>
                    {t.Direction || t.direction}
                  </MDTypography>
                )}
              </MDBox>
            ) : (
              <MDTypography variant="caption" color="secondary">○ No data yet</MDTypography>
            )}
          </MDBox>
        }
        date="updated 4 min ago"
        chart={{
          labels: data?.datesArray,
          datasets: { title, data: data?.scoreArray },
        }}
      />
    </MDBox>
  );
};

export default LineChart;
